
export interface LoginResponse {
  success: boolean;
  message?: string;
  token?: string;
  user?: any;
}

/**
 * 免密登录（使用code）- SDK专用轻量版
 */
export const loginByCode = async (baseUrl: string, loginData: { code: string; origin: string }): Promise<LoginResponse> => {
  try {
    const { origin, code } = loginData;
    if (!origin || !code) {
      return {
        success: false,
        message: '登录码或来源不能为空',
      };
    }

    // 处理 baseUrl 结尾的斜杠
    const apiBase = baseUrl && baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : (baseUrl || '');
    // 如果没有配置baseUrl，假设是当前域名的 /api
    const url = apiBase ? `${apiBase}/api/auth/login-by-code` : '/api/auth/login-by-code';

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        code,
        enterpriseCode: origin,
      }),
    });

    const data = await response.json();

    return {
      success: data.success,
      message: data.message,
      token: data.data?.token,
      user: data.data?.user,
    };
  } catch (error: any) {
    console.error('SDK: 免密登录请求失败:', error);
    return {
      success: false,
      message: error.message || '网络错误',
    };
  }
};

/**
 * 设置外部变量 - SDK专用轻量版
 */
export const setExternalVariables = async (baseUrl: string, variables: Record<string, any>, token?: string): Promise<any> => {
  try {
    // 将对象转换为后端期望的数组格式: [{ keyword: 'key', value: 'value' }]
    const variableArray = Object.entries(variables).map(([key, value]) => ({
      keyword: key,
      value: String(value) // 确保值是字符串
    }));

    const apiBase = baseUrl && baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : (baseUrl || '');
    const url = apiBase ? `${apiBase}/api/external/variables` : '/api/external/variables';

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ variable: variableArray }),
    });

    return await response.json();
  } catch (error: any) {
    console.error('SDK: 设置外部变量失败:', error);
    // 不抛出错误，以免影响页面加载
    return { success: false, message: error.message };
  }
};

/**
 * 创建会话 - SDK专用轻量版
 */
export const createSession = async (baseUrl: string, token: string, sessionData: { id: string; agentId: string; title: string }): Promise<any> => {
  try {
    const apiBase = baseUrl && baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : (baseUrl || '');
    const url = apiBase ? `${apiBase}/api/sessions` : '/api/sessions';

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(sessionData),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.json();
  } catch (error: any) {
    console.error('SDK: 创建会话失败:', error);
    throw error;
  }
};

/**
 * 发送消息给智能体（SDK专用轻量版）
 */
export const sendMessageToAgent = async (
  baseUrl: string,
  token: string,
  agentId: string,
  message: string,
  sessionId: string,
  onMessage: (content: string, responseType?: string, messageId?: string, reasoning?: string, isComplete?: boolean) => void,
  onComplete: () => void,
  onError: (error: Error) => void,
  signal?: AbortSignal,
  displayContent?: string, // 用户消息展示文本，透传给后端入库
) => {
  try {
    if (!token) {
      throw new Error('SDK: 未提供认证Token');
    }

    const apiBase = baseUrl && baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : (baseUrl || '');
    const url = apiBase ? `${apiBase}/api/messages/stream` : '/api/messages/stream';

    // 构建请求数据
    const requestData: Record<string, any> = {
      sessionId,
      agentId,
      content: message,
      type: 'assistant',
    };
    if (displayContent) {
      requestData.displayContent = displayContent;
    }

    console.log('SDK: 发送SSE请求:', { url, data: requestData });

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'Cache-Control': 'no-cache'
      },
      body: JSON.stringify(requestData),
      signal
    });

    if (!response.ok) {
      // 429: 会话正忙，后端 Redis 锁拦截了并发请求
      if (response.status === 429) {
        let errorMessage = '当前会话正在处理中，请稍后重试或开启新对话';
        try {
          const body = await response.json();
          if (body.message) errorMessage = body.message;
        } catch { /* 解析失败，使用默认提示 */ }
        const error = new Error(errorMessage);
        (error as any).code = 'SESSION_BUSY';
        throw error;
      }
      throw new Error(`HTTP错误: ${response.status} ${response.statusText}`);
    }

    const reader = response.body?.getReader();
    if (!reader) {
      throw new Error('无法获取响应流');
    }

    const decoder = new TextDecoder();
    let buffer = '';
    
    // 设置超时处理（支持深度思考模型的长响应时间）
    let timeoutId: any;
    const TIMEOUT_DURATION = 180000; // 3分钟超时

    const resetTimeout = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        console.log('SDK: SSE连接超时（长时间无响应）');
        // 取消读取
        reader.cancel('Timeout');
        onError(new Error('SSE连接超时'));
      }, TIMEOUT_DURATION);
    };

    // 初始化超时计时器
    resetTimeout();

    const processChunk = () => {
      // 收到数据，重置超时
      resetTimeout();

      // 处理SSE数据格式
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // 保留最后一行（可能不完整）

      for (const raw of lines) {
        const line = raw.trim();
        if (!line) continue;

        // 支持 data: 前缀
        let payload = '';
        if (line.startsWith('data:')) {
          payload = line.slice(5).trim();
        } else if (line.trim().startsWith('{') || line.trim().startsWith('[')) {
          // 兼容非标准 SSE 格式：直接返回 JSON
          payload = line.trim();
        } else {
          continue;
        }

        if (!payload) continue;

        // 结束信号
        if (payload === '[DONE]') {
          clearTimeout(timeoutId);
          onComplete();
          return true; // 表示结束
        }

        // 尝试解析JSON
        try {
          const jsonData = JSON.parse(payload);
          if (jsonData && typeof jsonData === 'object') {
            // 检查错误信息
            if (jsonData.error) {
              console.error('SDK: SSE错误:', jsonData.error);
              onError(new Error(jsonData.error));
              return true; // 结束处理
            }
            
            // 检查各种可能的内容字段
            const content = jsonData.content || jsonData.responseAll || jsonData.response || jsonData.message || jsonData.text;
            const reasoning = jsonData.reasoning_content;

            if ((content && typeof content === 'string') || (reasoning && typeof reasoning === 'string')) {
              const responseType = jsonData.responseType || 'text';
              const isComplete = jsonData.isComplete === true;
              onMessage(content || '', responseType, jsonData.id, reasoning, isComplete);
            }
          }
        } catch (parseError) {
          // 非 JSON，按纯文本处理
          if (payload.trim()) {
            onMessage(payload);
          }
        }
      }
      return false; // 表示继续
    };

    try {
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          break;
        }

        // 解码数据块
        const chunk = decoder.decode(value, { stream: true });
        buffer += chunk;

        // 立即处理当前缓冲区中的数据
        const shouldEnd = processChunk();
        if (shouldEnd) {
          return;
        }
      }

      // 处理剩余的缓冲区数据
      if (buffer.trim()) {
        processChunk();
      }

      clearTimeout(timeoutId);
      onComplete();
    } finally {
      reader.releaseLock();
      clearTimeout(timeoutId);
    }

  } catch (error: any) {
    if (error?.name === 'AbortError') {
      console.log('SDK: SSE请求已被取消');
      onComplete();
      return;
    }
    // 忽略服务暂时不可用的报错(主动取消时后端也可能返回)
    if (error?.message?.includes('服务暂时不可用')) {
      console.log('SDK: 忽略服务暂时不可用的报错(可能是主动取消导致)');
      onComplete();
      return;
    }
    console.error('SDK: 聊天请求失败:', error);
    onError(error);
  }
};
